import { PHI_FIELDS_TO_EXCLUDE } from './constants';

/**
 * Optimized PHI field detection using string operations where possible
 * Only use regex when pattern matching is truly needed
 */

// Fields that start with these prefixes (case-insensitive)
const PHI_PREFIXES = [
  'patient_name',
  'provider_name',
  'physician_name',
  'doctor_name',
  'referring_provider',
  'emergency_contact_name',
];

// Fields that contain these substrings anywhere (case-insensitive)
const PHI_SUBSTRINGS = [
  'email',
  'phone',
  'address',
  'ssn',
  'dob',
  'birth',
  'diagnosis',
  'medication',
  'treatment',
  'prescription',
  'allerg',  // catches allergy, allergies, allergic
  'insurance',
  'medical',
  'clinical',
  'symptom',
  'complaint',
  'history',
  'transcript',
  'comment',
  'description',
];

// Exact matches (case-insensitive)
const PHI_EXACT_MATCHES = new Set([
  'form_title',
  'test_name',
  'appointment_type_name',
  'update_reason',
]);

// Analytics-approved fields that are safe to track (not PHI)
// These fields have been reviewed and approved for analytics tracking
const ANALYTICS_APPROVED_FIELDS = new Set([
  'template_name',
  'section_name',
  'section_type',
  'template_type',
]);

// Complex patterns that actually need regex
const PHI_REGEX_PATTERNS = [
  /.*member.*id/i,       // member_id, membership_id, etc.
  /.*claim.*detail/i,    // claim_details, claims_detail_info
  /.*note.*content/i,    // note_content, notes_content
  /.*free.*text/i,       // free_text, freetext_field
  /form.*response/i,     // form_response but not response_time
  /.*answer$/i,          // fields ending in 'answer'
];

/**
 * Checks if a field name might contain PHI based on optimized checks
 * Performance optimized: O(1) for exact matches, O(n) for substrings, regex only when needed
 */
export function isPotentialPHIField(fieldName: string): boolean {
  const lowerField = fieldName.toLowerCase();

  // 0. Check analytics-approved fields first (these are explicitly safe)
  if (ANALYTICS_APPROVED_FIELDS.has(lowerField)) {
    return false; // Not PHI, safe to include
  }

  // 1. Check explicit exclusion list first (most specific)
  if ((PHI_FIELDS_TO_EXCLUDE as readonly string[]).includes(lowerField)) {
    return true;
  }

  // 2. Check exact matches (O(1) with Set)
  if (PHI_EXACT_MATCHES.has(lowerField)) {
    return true;
  }

  // 3. Check prefixes (common pattern for name fields)
  for (const prefix of PHI_PREFIXES) {
    if (lowerField.startsWith(prefix)) {
      return true;
    }
  }

  // 4. Check substrings (common PHI indicators)
  for (const substring of PHI_SUBSTRINGS) {
    if (lowerField.includes(substring)) {
      return true;
    }
  }

  // 5. Only use regex for complex patterns that can't be simplified
  for (const pattern of PHI_REGEX_PATTERNS) {
    if (pattern.test(fieldName)) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if a value might contain PHI based on patterns
 * Simplified since we control all input values
 */
export function isPotentialPHIValue(value: string): boolean {
  // Only check for obvious PHI patterns like SSN and email
  // Since we control all inputs, this is just a safety net
  
  // Check for SSN patterns (with or without dashes)
  if (/^\d{9}$/.test(value) || /^\d{3}-\d{2}-\d{4}$/.test(value)) {
    return true;
  }
  
  // Check for email addresses
  if (/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/.test(value)) {
    return true;
  }
  
  return false;
}

/**
 * Result type for sanitization to distinguish between PHI and invalid values
 */
type SanitizeResult = {
  success: true;
  value: string;
} | {
  success: false;
  reason: 'phi' | 'invalid' | 'null';
};

/**
 * Sanitizes a value to ensure it doesn't contain PHI
 * Returns a result object to distinguish between different failure reasons
 */
function sanitizeValue(key: string, value: unknown): SanitizeResult {
  if (value === null || value === undefined) {
    return { success: false, reason: 'null' };
  }

  let stringValue: string;

  if (typeof value === 'string') {
    stringValue = value;
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    stringValue = String(value);
  } else if (value instanceof Date) {
    stringValue = value.toISOString();
  } else {
    // For objects and arrays, stringify them
    try {
      stringValue = JSON.stringify(value);
    } catch {
      console.warn(`[Analytics] Could not stringify value for key: ${key}`);
      return { success: false, reason: 'invalid' };
    }
  }

  // Check if the string value might contain PHI
  if (isPotentialPHIValue(stringValue)) {
    console.warn(`[Analytics] Potential PHI detected in value for key: ${key}`);
    return { success: false, reason: 'phi' };
  }
  
  // Special handling for URLs that might contain patient identifiers
  // This is important since we pass 'path' in router tracking
  if (key.includes('url') || key.includes('path')) {
    // Redact any patient identifiers from URLs
    stringValue = stringValue
      .replace(/\/patients?\/[\w-]+/gi, '/patient/[REDACTED]')
      .replace(/patient[_-]?id=[\w-]+/gi, 'patient_id=[REDACTED]')
      .replace(/mrn=[\w-]+/gi, 'mrn=[REDACTED]');
  }
  
  // Since we removed form_title and similar fields, name detection is rarely needed
  // Keep minimal checking for any future fields that might slip through
  if (key.includes('title') || key.includes('label') || key.includes('name')) {
    // Simple check for obvious person names (First Last pattern)
    // This is just a safety net since we don't pass these fields anymore
    stringValue = stringValue.replace(
      /\b[A-Z][a-z]+\s+[A-Z][a-z]+\b/g,
      (match) => {
        // Don't redact common medical terms
        const commonTerms = ['Blood Pressure', 'Heart Rate', 'Physical Exam', 
                           'Emergency Contact', 'Lab Order', 'Follow Up'];
        return commonTerms.includes(match) ? match : '[NAME_REDACTED]';
      }
    );
  }
  
  return { success: true, value: stringValue };
}

/**
 * Sanitizes metadata to ensure HIPAA compliance while preserving key structure
 * Keys are preserved but values are sanitized or redacted as needed
 */
export function sanitizeMetadata(
  metadata?: Record<string, unknown>,
): Record<string, string> {
  if (!metadata) return {};

  const sanitized: Record<string, string> = {};

  Object.entries(metadata).forEach(([key, value]) => {
    if (isPotentialPHIField(key)) {
      // Redact PHI fields immediately
      sanitized[key] = `[REDACTED - PHI field]`;
      console.warn(`[Analytics] Redacted PHI field: ${key}`);
    } else {
      // Sanitize all other fields consistently
      const result = sanitizeValue(key, value);
      if (result.success) {
        sanitized[key] = result.value;
      } else {
        // Provide specific redaction message based on reason
        switch (result.reason) {
          case 'phi':
            sanitized[key] = '[REDACTED - PHI detected]';
            break;
          case 'invalid':
            sanitized[key] = '[REDACTED - Invalid value]';
            break;
          case 'null':
            // For null/undefined, we could optionally skip the key
            // but preserving it helps with debugging
            sanitized[key] = '[REDACTED - No value]';
            break;
        }
      }
    }
  });

  return sanitized;
}

/**
 * Validates that an event name is not accidentally including PHI
 */
export function validateEventName(eventName: string): boolean {
  const lowerEventName = eventName.toLowerCase();
  
  // Check for common PHI patterns in event names
  const phiPatterns = [
    /patient_\d+/, // Patient IDs
    /mrn_\d+/, // Medical record numbers
    /\b\d{3}-\d{2}-\d{4}\b/, // SSN pattern
    /\b[a-zA-Z]+_[a-zA-Z]+\b.*@/, // Email patterns
  ];
  
  for (const pattern of phiPatterns) {
    if (pattern.test(lowerEventName)) {
      console.error(`[Analytics] Event name may contain PHI: ${eventName}`);
      return false;
    }
  }
  
  return true;
}

/**
 * Anonymizes user IDs by hashing them
 * This is useful when we want to track unique users without storing actual IDs
 */
export async function hashUserId(userId: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(userId);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  return hashHex.substring(0, 16); // Return first 16 chars for brevity
}

/**
 * Sanitizes error messages to remove any potential PHI
 */
export function sanitizeErrorMessage(error: unknown): string {
  if (!error) return 'Unknown error';
  
  let message = '';
  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  } else {
    message = 'Error occurred';
  }
  
  // Remove any potential PHI patterns from error messages
  message = message
    .replace(/patient[_\s]+\S+/gi, 'patient_[REDACTED]')
    .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[SSN_REDACTED]')
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL_REDACTED]')
    .replace(/\b\d{10}\b/g, '[PHONE_REDACTED]');
  
  return message;
}

/**
 * Creates safe metadata for common tracking scenarios
 */
export const createSafeMetadata = {
  timing: (duration: number, label?: string) => ({
    duration_ms: String(Math.round(duration)),
    ...(label && { action_type: label }),
  }),
  
  error: (error: unknown, context?: string) => ({
    error_message: sanitizeErrorMessage(error),
    ...(context && { error_type: context }),
  }),
  
  feature: (featureName: string, action: string, value?: unknown) => ({
    feature: featureName,
    action_type: action,
    ...(value !== undefined && {
      count: (() => {
        if (value === null) return undefined;
        if (typeof value === 'string') return value;
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        // Handle all objects including arrays
        return JSON.stringify(value);
      })(),
    }),
  }),
  
  performance: (metric: string, value: number, unit = 'ms') => ({
    metric,
    [`${metric}_${unit}`]: Math.round(value).toString(),
  }),
};